import proj4 from 'proj4';
import { CoordinateArray, ProjectionType } from 'types';
import { GeoJSONFeature } from 'types/geojson';
import WKT from 'wkt';
import wkx from 'wkx';

export default class geometry {
    private sourceProjection: string;
    private targetProjection: string;

    constructor(source: string | ProjectionType, target: string | ProjectionType) {
        this.sourceProjection = this.getProjectionName(source || 'EPSG:4326');
        this.targetProjection = this.getProjectionName(target || 'EPSG:4326');
    }

    private getProjectionName(projection: string | ProjectionType): string {
        if (typeof projection === 'string') {
            return projection;
        }
        proj4.defs(projection.name, projection.value);
        return projection.name;
    }

    transformer(
        coordinates: CoordinateArray[] | CoordinateArray,
        source?: string | ProjectionType,
        target?: string | ProjectionType
    ) {
        let sourceProjection: string | null = null;
        let targetProjection: string | null = null;
        if (source) {
            sourceProjection = this.getProjectionName(source);
        }
        if (target) {
            targetProjection = this.getProjectionName(target);
        }

        if (Array.isArray(coordinates)) {
            if (Array.isArray(coordinates[0])) {
                return (coordinates as CoordinateArray[]).map((coordinate) =>
                    proj4(
                        sourceProjection ? sourceProjection : this.sourceProjection,
                        targetProjection ? targetProjection : this.targetProjection,
                        coordinate
                    )
                );
            }
            return proj4(
                sourceProjection ? sourceProjection : this.sourceProjection,
                targetProjection ? targetProjection : this.targetProjection,
                coordinates
            );
        }

        throw new Error('Invalid input: coordinates must be an array.');
    }

    parseWKT(wktString: string): GeoJSONFeature {
        return { type: 'Feature', properties: {}, geometry: WKT.parse(wktString) };
    }

    parseWKB(wkbString: string): GeoJSONFeature {
        //@ts-ignore
        return {
            type: 'Feature',
            properties: {},
            //@ts-ignore
            geometry: wkx.Geometry.parse(Buffer.from(wkbString, 'hex')).toGeoJSON(),
        };
    }

    transformerGEOJSON(geoJson: GeoJSONFeature): GeoJSONFeature | null {
        try {
            const geometry = geoJson.geometry;
            if (!geometry) {
                throw new Error('No geometry found in GeoJSON');
            }

            switch (geometry.type) {
                case 'Point':
                    geometry.coordinates = this.transformer([geometry.coordinates])[0];
                    break;
                case 'LineString':
                    geometry.coordinates = this.transformer(geometry.coordinates);
                    break;
                case 'Polygon':
                    geometry.coordinates = geometry.coordinates.map((ring: CoordinateArray[]) =>
                        this.transformer(ring)
                    );
                    break;
                case 'MultiPoint':
                case 'MultiLineString':
                    // geometry.coordinates = geometry.coordinates.map(
                    //     (coords: CoordinateArray[]) => this.transformer(coords)
                    // );
                    break;
                case 'MultiPolygon':
                    geometry.coordinates = geometry.coordinates.map((polygon: CoordinateArray[][]) =>
                        polygon.map((ring: CoordinateArray[]) => this.transformer(ring))
                    );
                    break;
                default:
                    throw new Error('Unsupported geometry type');
            }

            return geoJson;
        } catch (error) {
            console.error('Error processing GeoJSON:', error);
            return null;
        }
    }

    /**
     * Calculate the length of a LineString feature.
     * @param coordinates - Array of coordinates (UTM).
     * @returns Length in meters.
     */
    getLength = (coordinates: CoordinateArray[]): number => {
        let length = 0;

        for (let i = 1; i < coordinates.length; i++) {
            const [x1, y1] = coordinates[i - 1];
            const [x2, y2] = coordinates[i];
            length += Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
        }

        return length;
    };

    /**
     * Calculate the bearing of a LineString feature.
     * @param coordinates - Array of coordinates (UTM).
     * @returns Bearing in degree.
     */
    getBearing = (coordinates: CoordinateArray[]): number => {
        if (coordinates.length < 2) return parseFloat('nan');

        const [x1, y1] = coordinates[0];
        const [x2, y2] = coordinates[coordinates.length - 1];

        const dx = x2 - x1;
        const dy = y2 - y1;

        const angleRad = Math.atan2(dx, dy); // Notice dx and dy order!
        let angleDeg = angleRad * (180 / Math.PI);

        // Convert from [-180, 180] to [0, 360]
        if (angleDeg < 0) angleDeg += 360;

        return angleDeg;
    };

    /**
     * Calculate the area of a Polygon feature.
     * @param coordinates - Array of coordinate rings (UTM).
     * @returns Area in square meters.
     */
    getArea = (coordinates: CoordinateArray[][]): number => {
        let area = 0;

        coordinates.forEach((ring) => {
            let ringArea = 0;
            for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
                const [x1, y1] = ring[j];
                const [x2, y2] = ring[i];
                ringArea += x1 * y2 - x2 * y1;
            }
            area += Math.abs(ringArea) / 2;
        });

        return area;
    };

    /**
     * Calculate the midpoint of a geometry
     * @param feature GeoJSONFeature
     * @returns Midpoint coordinate in UTM [easting, northing]
     */
    getMidpoint(feature: GeoJSONFeature): CoordinateArray {
        switch (feature.geometry.type) {
            case 'Point':
                // A Point's midpoint is itself
                return feature.geometry.coordinates as CoordinateArray;

            case 'LineString': {
                const lineCoords = feature.geometry.coordinates as CoordinateArray[];
                let totalDistance = 0;
                let midpointDistance = 0;

                // Calculate total line length
                for (let i = 1; i < lineCoords.length; i++) {
                    totalDistance += this.distance(lineCoords[i - 1], lineCoords[i]);
                }

                // Find the segment containing the midpoint
                midpointDistance = totalDistance / 2;
                let accumulatedDistance = 0;

                for (let i = 1; i < lineCoords.length; i++) {
                    const segmentDistance = this.distance(lineCoords[i - 1], lineCoords[i]);
                    accumulatedDistance += segmentDistance;
                    if (accumulatedDistance >= midpointDistance) {
                        const excessDistance = accumulatedDistance - midpointDistance;
                        const t = 1 - excessDistance / segmentDistance; // Interpolation factor
                        return this.interpolate(lineCoords[i - 1], lineCoords[i], t);
                    }
                }
                break;
            }

            case 'Polygon': {
                const polygonCoords = feature.geometry.coordinates as CoordinateArray[][];
                const outerRing = polygonCoords[0]; // Outer ring of the polygon

                // Calculate centroid of the outer ring
                return this.getCentroid(outerRing);
            }

            default:
                throw new Error('Unsupported geometry type');
        }
        throw new Error('Unable to calculate midpoint');
    }

    /**
     * Calculate the distance between two UTM coordinates
     * @param coord1 First coordinate
     * @param coord2 Second coordinate
     * @returns Distance in meters
     */
    distance(coord1: CoordinateArray, coord2: CoordinateArray): number {
        const dx = coord2[0] - coord1[0];
        const dy = coord2[1] - coord1[1];
        return Math.sqrt(dx * dx + dy * dy);
    }

    /**
     * Interpolate between two coordinates
     * @param coord1 Start coordinate
     * @param coord2 End coordinate
     * @param t Interpolation factor (0 to 1)
     * @returns Interpolated coordinate
     */
    interpolate(coord1: CoordinateArray, coord2: CoordinateArray, t: number): CoordinateArray {
        return [coord1[0] + t * (coord2[0] - coord1[0]), coord1[1] + t * (coord2[1] - coord1[1])];
    }

    isPointInPolygon(point: GeoJSONFeature, polygon: GeoJSONFeature) {
        if (point.geometry.type !== 'Point') {
            throw new Error('Input point must be GeoJSON Point feature');
        }

        if (polygon.geometry.type !== 'Polygon') {
            throw new Error('Input polygon must be GeoJSON Polygon feature');
        }

        const [x, y] = point.geometry.coordinates;
        const rings = polygon.geometry.coordinates;

        // Ray casting for a single ring
        const isInsideRing = (ring) => {
            let inside = false;

            for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
                const [xi, yi] = ring[i];
                const [xj, yj] = ring[j];

                const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;

                if (intersect) inside = !inside;
            }

            return inside;
        };

        // First ring = outer boundary
        if (!isInsideRing(rings[0])) return false;

        // Holes (if inside any hole → false)
        for (let i = 1; i < rings.length; i++) {
            if (isInsideRing(rings[i])) return false;
        }

        return true;
    }

    /**
     * Calculate the centroid of a linear ring (polygon outer ring)
     * @param ring Coordinates of the linear ring
     * @returns Centroid coordinate
     */
    getCentroid(ring: CoordinateArray[]): CoordinateArray {
        let centroidX = 0,
            centroidY = 0,
            area = 0;

        for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
            const [x1, y1] = ring[j];
            const [x2, y2] = ring[i];
            const a = x1 * y2 - x2 * y1;
            centroidX += (x1 + x2) * a;
            centroidY += (y1 + y2) * a;
            area += a;
        }

        area *= 0.5;
        centroidX /= 6 * area;
        centroidY /= 6 * area;

        return [centroidX, centroidY];
    }

    createFeatureCollection(features: GeoJSONFeature[]) {
        return {
            type: 'FeatureCollection',
            features,
        };
    }

    getBBoxFromGeoJSON(features: (GeoJSONFeature | null)[]): number[] {
        let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;

        const updateBounds = (coords: number[]) => {
            const [x, y] = coords;
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
        };

        const processCoordinates = (coords: any) => {
            if (typeof coords[0] === 'number') {
                // Single coordinate pair [x, y]
                updateBounds(coords as number[]);
            } else {
                // Nested coordinates (e.g., [[x1, y1], [x2, y2]] or deeper)
                coords.forEach(processCoordinates);
            }
        };

        // Iterate through features
        features.forEach((feature: any) => {
            const geometry = feature.geometry;
            if (geometry.type === 'Point') {
                updateBounds(geometry.coordinates);
            } else if (geometry.type === 'MultiPoint' || geometry.type === 'LineString') {
                processCoordinates(geometry.coordinates);
            } else if (geometry.type === 'Polygon' || geometry.type === 'MultiLineString') {
                geometry.coordinates.forEach(processCoordinates);
            } else if (geometry.type === 'MultiPolygon') {
                geometry.coordinates.forEach((polygon: any) => polygon.forEach(processCoordinates));
            }
        });

        return [minX, minY, maxX, maxY];
    }
}
